import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { useDeleteDocumentMutation, useReplaceDocumentMutation } from 'api/main';
import { checkAcceptedFileTypes, validateFileSize } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import IdDocumentDialog from '../IdDocumentDialog';
import { CustomButton, FileErrorMessage, FileInputButton, Loader } from 'components';
import { IconButton } from '@mui/material';
import * as icons from 'mdi-material-ui';

const DocumentActionButtons = ({
    doc,
    isReplaceable,
    canArchive,
    isSecondId,
    isJumioType,
    isFirstId,
    userIsInvestor,
    useJumioIframe,
    setReplaceIframeVisible,
    isRemovable,
    isSmallScreen,
    onChange,
    setUploadedFile,
}) => {
    const [fileSizeError, setFileSizeError] = useState(false);
    const [fileTypeError, setFileTypeError] = useState(false);
    const [idReplaceDialogOpen, setIdReplaceDialogOpen] = useState(false);
    const [replacingDocId, setReplacingDocId] = useState(null);

    const [deleteDocument, { isLoading: removeDocLoader }] = useDeleteDocumentMutation();
    const [
        replaceDocument,
        { isLoading: replaceLoading, isSuccess: replaceSuccess, reset: replaceReset },
    ] = useReplaceDocumentMutation();

    const handleFileChange = useCallback(
        e => {
            const file = e.target.files[0];
            checkAcceptedFileTypes(
                file,
                () => {
                    setFileTypeError(false);
                    if (validateFileSize(file)) {
                        setFileSizeError(false);
                        setUploadedFile?.(file);
                        const formData = new FormData();
                        formData.append('file', file);
                        replaceDocument({ id: doc.id, formData, updateUser: true });
                    } else {
                        setFileSizeError(true);
                        setUploadedFile?.(null);
                    }
                },
                () => {
                    setUploadedFile?.(null);
                    setFileTypeError(true);
                    setFileSizeError(false);
                }
            );
        },
        [replaceDocument, doc.id, setUploadedFile]
    );

    useEffect(() => {
        if (replaceSuccess) {
            replaceReset();
            onChange?.();
        }
    }, [replaceSuccess, replaceReset, onChange]);

    const handleRemoveDocument = useCallback(() => {
        deleteDocument(doc.id);
    }, [deleteDocument, doc.id]);

    const handleOpenIdReplaceDialog = useCallback(
        id => () => {
            setIdReplaceDialogOpen(true);
            setReplacingDocId(id);
        },
        []
    );

    const handleCloseIdReplaceDialog = useCallback(() => {
        setIdReplaceDialogOpen(false);
        setReplacingDocId(null);
    }, []);

    return (
        <React.Fragment>
            {(isReplaceable || canArchive) &&
                !isSecondId &&
                (isJumioType ? (
                    <CustomButton
                        noMargin
                        id={`replace-button-${doc.id}`}
                        label={canArchive ? texts.REPLACE_AND_ARCHIVE : texts.REPLACE}
                        sx={{
                            padding: '2px 5px',
                            marginLeft: '20px',
                            marginRight: '20px',
                            width: 'fit-content',
                            height: 'fit-content',
                            '@media (min-width:351px) and (max-width:720px)': {
                                margin: '10px 0 0 0',
                            },
                            '@media (max-width:351px)': {
                                marginLeft: '0 !important',
                            },
                        }}
                        onClick={
                            isFirstId && !userIsInvestor && !useJumioIframe
                                ? handleOpenIdReplaceDialog(doc.id)
                                : () => setReplaceIframeVisible(true)
                        }
                    />
                ) : (
                    <Loader
                        loading={replaceLoading}
                        marginTop={0}
                        pl={2}
                        alignItems="flex-start"
                        size={20}
                    >
                        <FileInputButton
                            noMargin
                            id={`replace-button-${doc.id}`}
                            label={canArchive ? texts.REPLACE_AND_ARCHIVE : texts.REPLACE}
                            onFileChange={handleFileChange}
                            buttonClassName={{
                                padding: '2px 5px !important',
                                marginLeft: '20px !important',
                                width: 'fit-content',
                                height: 'fit-content',
                                '@media (max-width:720px)': {
                                    margin: '5px 0 5px 0 !important',
                                },
                            }}
                        />
                        {fileSizeError && <FileErrorMessage variant="size" />}
                        {fileTypeError && <FileErrorMessage variant="type" />}
                    </Loader>
                ))}
            <IdDocumentDialog
                open={idReplaceDialogOpen}
                id={replacingDocId}
                handleClose={handleCloseIdReplaceDialog}
            />
            {isRemovable &&
                (isSmallScreen ? (
                    <CustomButton
                        noMargin
                        id={`remove-button-${doc.id}`}
                        label={texts.SUPPRIMER}
                        sx={{
                            padding: '2px 5px !important',
                            marginLeft: '20px !important',
                            width: 'fit-content',
                            height: 'fit-content',
                            '@media (max-width:720px)': {
                                margin: '5px 0 5px 0 !important',
                            },
                        }}
                        onClick={handleRemoveDocument}
                        disabled={removeDocLoader}
                        loading={removeDocLoader}
                    />
                ) : (
                    <IconButton
                        disableRipple
                        size="small"
                        id={`remove-button-${doc.id}`}
                        onClick={handleRemoveDocument}
                        sx={{
                            marginLeft: '10px',
                            height: 'fit-content',
                            marginTop: '-3px',
                        }}
                        disabled={removeDocLoader}
                    >
                        <icons.Delete />
                    </IconButton>
                ))}
        </React.Fragment>
    );
};

DocumentActionButtons.propTypes = {
    doc: PropTypes.object.isRequired,
    isReplaceable: PropTypes.bool,
    canArchive: PropTypes.bool,
    isSecondId: PropTypes.bool,
    isJumioType: PropTypes.bool,
    isFirstId: PropTypes.bool,
    userIsInvestor: PropTypes.bool,
    useJumioIframe: PropTypes.bool,
    setReplaceIframeVisible: PropTypes.func.isRequired,
    isRemovable: PropTypes.bool,
    isSmallScreen: PropTypes.bool,
    onChange: PropTypes.func,
    setUploadedFile: PropTypes.func,
};

export default DocumentActionButtons;
